import {inject, Injectable} from '@angular/core';
import {HttpClient, HttpErrorResponse, HttpResponse} from '@angular/common/http';
import {catchError, delay, map, mergeMap, Observable, of, switchMap, throwError} from 'rxjs';
import {ApiUrlService} from "./api-url.service";
import {AnhangUploadStatusEnum, UploadStatus} from "../../model/upload-status";
import {AnhangMetadaten} from "../../model/nachricht/nachricht";
import {environment} from "../../../../environments/environment";


@Injectable({providedIn: 'root'})
export class UploadService {

    private readonly http: HttpClient = inject(HttpClient);
    private apiUrlService = inject(ApiUrlService);

    private readonly NACHRICHT_ID_HEADER = 'Location';
    private readonly VIRUS_INFECTED_HEADER = 'X-Virus-Infected';

    uploadFile(entwurfId: string, file: File): Observable<string> {
      //&  console.log(`[UploadService] Uploading file: ${file.name}`);
        const formData = new FormData();
        formData.append('file', file);

        return this.http.post(`${this.apiUrlService.getUploadApiUrl()}/ordner/entwurf/nachrichten/${entwurfId}/anhaenge`, formData, {observe: 'response'})
            .pipe(
                map((resp: HttpResponse<any>) => {
                    const loc = resp.headers.get(this.NACHRICHT_ID_HEADER);
                    if (!loc) throw new Error('No Location header in response');
                    return loc;
                }),
                catchError((err: HttpErrorResponse) => {
                    // 403 + x-virus-infected Header → VirusDetectionError
                    if (err.status === 403 && err.headers.get(this.VIRUS_INFECTED_HEADER)) {

                        return throwError(() => ({"virusInfected": true}));
                    }
                    // alle anderen weiterreichen
                    return throwError(() => err);
                })
            );

    }

    pollUploadStatus(nachrichtId: string, anhangId: string): Observable<UploadStatus> {
        const baseUrl = this.apiUrlService.getApiUrl();
        const statusUrl = `${baseUrl}/ordner/entwurf/nachrichten/${nachrichtId}/anhaenge/${anhangId}/upload-status`;
        const maxTries = environment.maxRetriesUpload || 499;

        const poll = (attempt: number): Observable<UploadStatus> => {
            return this.http.get<UploadStatus>(`${statusUrl}?try=${attempt}`).pipe(
                delay(attempt === 1 ? 0 : 1000),
                switchMap((res) => {
                    if (res.status !== AnhangUploadStatusEnum.IN_BEARBEITUNG) {
                        return of(res);
                    }

                    if (attempt >= maxTries) {
                        return this.deleteFile(nachrichtId, anhangId).pipe(
                            catchError(() => of(null)),
                            switchMap(() =>
                                throwError(() => new Error(`Polling wurde nach ${maxTries} Versuchen abgebrochen.`))
                            )
                        );
                    }

                    return poll(attempt + 1);
                })
            );
        };
        return poll(1);
    }

    /**
     * Löscht einen Anhang einer Nachricht
     */
    deleteFile(nachrichtId: string, anhangId: string): Observable<any> {
        return this.http.delete(`${this.apiUrlService.getApiUrl()}/ordner/entwurf/nachrichten/${nachrichtId}/anhaenge/${anhangId}`);
    }

    getAnhangMetadaten(nachrichtId: string, anhangId: string): Observable<AnhangMetadaten> {
        return this.http.get<AnhangMetadaten>(`${this.apiUrlService.getApiUrl()}/ordner/entwurf/nachrichten/${nachrichtId}/anhaenge/${anhangId}/metadaten`);
    }

}
